
import React from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, Heart, Calendar, Gift, Users } from "lucide-react";

const HeroSection3 = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      eyebrow: "Des preuves d'amour à l'épreuve du temps",
      headline: "Créez des souvenirs qui deviennent des cadeaux",
      subHeadline: "Transformez vos émotions d'aujourd'hui en moments magiques de demain",
      description: "Avec Legatia, offrez plus qu'un simple cadeau. Créez une capsule temporelle émotionnelle qui révélera vos messages, vidéos et contributions financières au moment parfait de leur vie.",
      primaryCTA: "Créer mon premier cadeau",
      secondaryCTA: "Découvrir comment ça marche",
      features: [
        { icon: Heart, text: "Messages personnalisés" },
        { icon: Calendar, text: "Révélation programmée" },
        { icon: Gift, text: "Contribution qui grandit" },
        { icon: Users, text: "Cadeau collectif" }
      ],
      trustText: "Déjà 1000+ familles nous font confiance"
    },
    en: {
      eyebrow: "A presence that transcends time",
      headline: "Create memories that become gifts",
      subHeadline: "Transform your emotions today into magical moments tomorrow",
      description: "With Legatia, give more than just a gift. Create an emotional time capsule that will reveal your messages, videos, and financial contributions at the perfect moment in their life.",
      primaryCTA: "Create my first gift",
      secondaryCTA: "Discover how it works",
      features: [
        { icon: Heart, text: "Personal messages" },
        { icon: Calendar, text: "Scheduled reveal" },
        { icon: Gift, text: "Growing contribution" },
        { icon: Users, text: "Collective gift" }
      ],
      trustText: "Already 1000+ families trust us"
    },
    de: {
      eyebrow: "Eine Präsenz, die die Zeit überdauert",
      headline: "Schaffen Sie Erinnerungen, die zu Geschenken werden",
      subHeadline: "Verwandeln Sie Ihre Emotionen von heute in magische Momente von morgen",
      description: "Mit Legatia schenken Sie mehr als nur ein Geschenk. Erstellen Sie eine emotionale Zeitkapsel, die Ihre Nachrichten, Videos und finanziellen Beiträge zum perfekten Moment in ihrem Leben enthüllt.",
      primaryCTA: "Mein erstes Geschenk erstellen",
      secondaryCTA: "Entdecken Sie, wie es funktioniert",
      features: [
        { icon: Heart, text: "Persönliche Nachrichten" },
        { icon: Calendar, text: "Geplante Enthüllung" },
        { icon: Gift, text: "Wachsender Beitrag" },
        { icon: Users, text: "Kollektives Geschenk" }
      ],
      trustText: "Bereits 1000+ Familien vertrauen uns"
    }
  };

  const textContent = content[language];

  return (
    <section className="relative min-h-screen flex items-center pt-20 pb-16 px-4 bg-gradient-to-br from-white via-legatia-light/30 to-legatia-yellow/10 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-gradient-to-l from-legatia-orange/8 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-gradient-to-r from-legatia-yellow/10 to-transparent rounded-full blur-2xl"></div>
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-12 gap-8 xl:gap-12 items-center">
          {/* Content Section */}
          <div className="lg:col-span-7 space-y-8">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-legatia-orange/10 border border-legatia-orange/20">
              <div className="w-2 h-2 rounded-full bg-legatia-orange animate-pulse"></div>
              <span className="text-sm font-medium text-legatia-orange">
                {textContent.eyebrow}
              </span>
            </div>
            
            {/* Headlines */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-gray-900 via-legatia-orange to-legatia-red bg-clip-text text-transparent">
                  {textContent.headline}
                </span>
              </h1>
              
              <h2 className="text-xl md:text-2xl lg:text-3xl text-gray-700 font-light leading-relaxed">
                {textContent.subHeadline}
              </h2>
            </div>
            
            {/* Description */}
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
              {textContent.description}
            </p>
            
            {/* Feature Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {textContent.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-white/60 border border-gray-100">
                  <div className="w-8 h-8 rounded-lg bg-legatia-orange/10 flex items-center justify-center">
                    <feature.icon className="w-4 h-4 text-legatia-orange" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{feature.text}</span>
                </div>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                className="group bg-gradient-to-r from-legatia-orange to-legatia-red hover:from-legatia-orange/90 hover:to-legatia-red/90 text-white px-8 py-6 rounded-2xl transition-all transform hover:shadow-xl hover:-translate-y-1 text-lg font-semibold shadow-lg"
                onClick={() => window.location.href = "https://app.getlegatia.ch/"}
              >
                {textContent.primaryCTA}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button 
                variant="outline" 
                className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-6 rounded-2xl transition-all hover:shadow-lg font-medium"
                onClick={() => {
                  document.getElementById("how-it-works")?.scrollIntoView({
                    behavior: "smooth"
                  });
                }}
              >
                {textContent.secondaryCTA}
              </Button>
            </div>
            
            {/* Trust Badge */}
            <div className="flex items-center gap-3 pt-4">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-legatia-orange to-legatia-red border-2 border-white"></div>
                ))}
              </div>
              <span className="text-sm text-gray-600 font-medium">
                {textContent.trustText}
              </span>
            </div>
          </div>
          
          {/* Visual Section */}
          <div className="lg:col-span-5">
            <div className="relative">
              {/* Main Image Container */}
              <div className="relative group">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-br from-legatia-yellow/20 to-legatia-orange/20 rounded-3xl transform rotate-3 group-hover:rotate-6 transition-transform duration-500"></div>
                
                {/* Image */}
                <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                  <img src="/lovable-uploads/8ba0af71-ff9f-4a0d-903b-27a7c9e31c38.png" alt="Une famille créant des souvenirs précieux ensemble" className="w-full h-full object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-700" loading="eager" decoding="async" />
                  
                  {/* Overlay with floating elements */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  
                  {/* Floating message bubble */}
                  <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-lg max-w-xs animate-float">
                    <div className="flex items-start gap-3">
                      <Heart className="w-5 h-5 text-legatia-red mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Message pour plus tard</p>
                        <p className="text-xs text-gray-600 mt-1">"Ma chérie, le jour où tu liras ceci..."</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating contribution indicator */}
                  <div className="absolute bottom-6 right-6 bg-legatia-orange/95 backdrop-blur-sm text-white p-3 rounded-xl shadow-lg">
                    <div className="flex items-center gap-2">
                      <Gift className="w-4 h-4" />
                      <span className="text-sm font-semibold">+500€</span>
                    </div>
                    <p className="text-xs opacity-90 mt-1">Grandit avec le temps</p>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-legatia-yellow/30 rounded-full animate-pulse-light"></div>
              <div className="absolute -bottom-6 -left-6 w-16 h-16 bg-legatia-green/20 rounded-full animate-float" style={{
              animationDelay: "1s"
            }}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection3;
